import React from 'react';

/**
 * Inline markup the external system embeds in comparison text: `{{…}}` is bold,
 * `[[…]]` is an underlined navy run. The two nest either way (`{{[[both]]}}`);
 * an unclosed marker stays literal rather than swallowing the rest of the text.
 */
const MARKUP_SOURCE = '\\{\\{([\\s\\S]*?)\\}\\}|\\[\\[([\\s\\S]*?)\\]\\]';

const LINK_COLOR = '#1a295a';

const renderRuns = (text: string, keyPrefix: string, accent?: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;

  // A fresh regex per call: a shared `/g` instance would have its lastIndex reset by the recursion.
  Array.from(text.matchAll(new RegExp(MARKUP_SOURCE, 'g'))).forEach((match) => {
    const index = match.index ?? 0;
    if (index > lastIndex) nodes.push(text.slice(lastIndex, index));

    const [, bold, underline] = match;
    const key = `${keyPrefix}${index}`;
    const inner = renderRuns(bold ?? underline, `${key}-`, accent);
    const color = accent ?? LINK_COLOR;
    nodes.push(
      bold !== undefined ? (
        <strong key={key} style={accent ? { color: accent } : undefined}>
          {inner}
        </strong>
      ) : (
        <u key={key} style={{ color, textDecorationColor: color }}>
          {inner}
        </u>
      ),
    );

    lastIndex = index + match[0].length;
  });

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

/** `accent` recolours marked runs, e.g. red on the competitor side; unset keeps them navy. */
export const renderRichText = (text: string, accent?: string): React.ReactNode => {
  const nodes = renderRuns(text, '', accent);
  return nodes.length > 0 ? nodes : text;
};
